<template>
  <main v-if="article" class="bh-page">
    <SimpleTopNav />

    <div class="breadcrumb"><a href="/">Home</a> &rsaquo; <a href="/blog">Blog</a> &rsaquo; {{ article.breadcrumb }}</div>

    <header class="article-hero">
      <span class="tag">{{ article.tag }}</span>
      <h1 v-html="article.title"></h1>
      <p class="meta" v-html="article.meta"></p>
    </header>

    <article class="article">
      <div class="wrap" v-html="article.content"></div>
    </article>

    <SimpleFooter show-blog />
  </main>
  <NotFoundView v-else />
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { blogArticleMap } from '@/data/blogArticles'
import SimpleTopNav from '@/components/SimpleTopNav.vue'
import SimpleFooter from '@/components/SimpleFooter.vue'
import NotFoundView from './NotFoundView.vue'

const route = useRoute()
const article = computed(() => blogArticleMap[route.meta.slug])
</script>

<style scoped>
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.bh-page {
  --gold: #d4a574;
  --gold-light: #f0d4a8;
  font-family: 'Inter', sans-serif;
  background: #080808;
  color: #e8e4df;
  line-height: 1.7;
  overflow-x: hidden;
}

a {
  color: inherit;
  text-decoration: none;
}

.wrap {
  max-width: 750px;
  margin: 0 auto;
  padding: 0 2rem;
}

.breadcrumb {
  padding: 5.5rem 2rem .5rem;
  max-width: 750px;
  margin: 0 auto;
  font-size: .75rem;
  color: rgba(255, 255, 255, .3);
}

.breadcrumb a {
  color: var(--gold);
  opacity: .6;
  transition: opacity .3s;
}

.breadcrumb a:hover {
  opacity: 1;
}

.article-hero {
  padding: 8rem 2rem 3rem;
  text-align: center;
}

.article-hero .tag {
  font-size: .7rem;
  color: var(--gold);
  letter-spacing: .2em;
  text-transform: uppercase;
  margin-bottom: 1rem;
  display: block;
}

.article-hero h1 {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.article-hero h1 :deep(em) {
  font-style: normal;
  color: var(--gold);
}

.article-hero .meta {
  color: rgba(255, 255, 255, .35);
  font-size: .8rem;
}

.article {
  padding: 0 2rem 5rem;
}

.article :deep(h2) {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.5rem;
  margin: 3rem 0 1.2rem;
  color: #fff;
}

.article :deep(h3) {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.15rem;
  margin: 2rem 0 .8rem;
  color: var(--gold);
}

.article :deep(p) {
  color: rgba(255, 255, 255, .6);
  font-size: .92rem;
  line-height: 1.9;
  margin-bottom: 1.2rem;
}

.article :deep(ul),
.article :deep(ol) {
  margin: 1rem 0 2rem;
  padding-left: 1.5rem;
}

.article :deep(li) {
  padding: .4rem 0;
  color: rgba(255, 255, 255, .6);
  font-size: .9rem;
}

.article :deep(strong) {
  color: rgba(255, 255, 255, .9);
}

.article :deep(.price-table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0 2rem;
}

.article :deep(.price-table th),
.article :deep(.price-table td) {
  padding: .9rem 1.2rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, .06);
  font-size: .88rem;
}

.article :deep(.price-table th) {
  color: var(--gold);
  font-weight: 600;
  font-size: .75rem;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.article :deep(.price-table td) {
  color: rgba(255, 255, 255, .65);
}

.article :deep(.price-table tr:hover td) {
  color: #fff;
}

.article :deep(.cta-box) {
  background: rgba(212, 165, 116, .05);
  border: 1px solid rgba(212, 165, 116, .15);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  margin: 3rem 0;
}

.article :deep(.cta-box h3) {
  font-family: 'Noto Serif JP', serif;
  color: #fff;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.article :deep(.cta-box p) {
  color: rgba(255, 255, 255, .5);
  font-size: .88rem;
  margin-bottom: 1.5rem;
}

.article :deep(.cta-box .btn) {
  display: inline-block;
  padding: 1rem 2.5rem;
  background: var(--gold);
  color: #000;
  font-weight: 600;
  font-size: .85rem;
  letter-spacing: .1em;
  transition: all .3s;
}

.article :deep(.cta-box .btn:hover) {
  background: var(--gold-light);
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .article-hero {
    padding: 6rem 1.5rem 2rem;
  }

  .article {
    padding: 0 1.5rem 4rem;
  }
}
</style>
